import { ProctoringMethod } from "@verity/proctoring-option/types";
import AutomatedIcon from "../images/method/automated.svg?react";
import LiveIcon from "../images/method/live.svg?react";
import LiveCheckInIcon from "../images/method/live-check-in.svg?react";
import PostReviewIcon from "../images/method/post-review.svg?react";
import CourseIcon from "../images/profiles/course.svg?react";
import InstitutionIcon from "../images/profiles/institution.svg?react";
import SubAccountIcon from "../images/profiles/subaccount.svg?react";
import PersonalIcon from "../images/profiles/personal.svg?react";
import AdvancedIcon from "../images/profiles/advanced.svg?react";
import StandardIcon from "../images/profiles/standard.svg?react";
import EssentialIcon from "../images/profiles/essential.svg?react";
import { ProctoringOptionProfileType } from "@verity/database/prisma";
import { PresetProfileType } from "../profile/profileHelpers";
import i18nPackage from "../../i18n/i18n";
import McGrawHill from "../images/third-party/McGraw-Hill.svg?react";
import Aleks from "../images/third-party/Aleks.svg?react";
import Pearson from "../images/third-party/Pearson.svg?react";
import Cengage from "../images/third-party/Cengage.svg?react";
import WWNorton from "../images/third-party/WWNorton.svg?react";
import Webwork from "../images/third-party/Webwork.svg?react";
import ZyBooks from "../images/third-party/ZyBooks.svg?react";
import Others from "../images/third-party/Others.svg?react";
import Wiley from "../images/third-party/Wiley.svg?react";
import WilloLabs from "../images/third-party/WilloLabs.svg?react";
import UCertify from "../images/third-party/uCertify.svg?react";
import MyOpenMath from "../images/third-party/MyOpenMath.svg?react";
import Macmillan from "../images/third-party/Macmillan.svg?react";
import LabyrinthLearning from "../images/third-party/LabyrinthLearning.svg?react";
import VHL from "../images/third-party/VHL.svg?react";
import AtomicJolt from "../images/third-party/AtomicJolt.svg?react";
import { Platform } from "@verity/url-matcher";
import ChromeIcon from "../images/platform/chrome.svg?react";
import EdgeIcon from "../images/platform/edge.svg?react";
import BraveIcon from "../images/platform/brave.svg?react";
import OperaIcon from "../images/platform/opera.svg?react";
import FirefoxIcon from "../images/platform/firefox.svg?react";
import SafariIcon from "../images/platform/safari.svg?react";
import WindowsIcon from "../images/platform/windows.svg?react";
import MacIcon from "../images/platform/mac.svg?react";
import { DisplayPlatform } from "../proctoring-options/option-text/ProctoringOptionText";
import ExtensionIcon from "../images/platform/extension.svg?react";
import LockdownIcon from "../images/platform/lockdown.svg?react";
import BrowserIcon from "../images/platform/browser.svg?react";

export const METHOD_ICON_CONFIG: Record<ProctoringMethod, typeof LiveIcon> = {
	"pop-in": LiveCheckInIcon,
	"pop-in-post-review": PostReviewIcon,
	automated: AutomatedIcon,
	live: LiveIcon,
};

export type ProfileConfigType = {
	Icon: typeof CourseIcon; // So VITE doesn't complain and hot reload everytime ProfileList is saved
	name: string;
	description: string;
	bgColor: string;
	svgColor: string;
};

export const PROFILE_CONFIG: Record<
	Exclude<ProctoringOptionProfileType, "assessment" | "preset">,
	ProfileConfigType
> & {
	preset: Record<PresetProfileType, ProfileConfigType>;
} = {
	course: {
		name: i18nPackage.t("editQuizPage.profileStep.course.name"),
		description: i18nPackage.t("editQuizPage.profileStep.course.desc"),
		Icon: CourseIcon,
		bgColor: "#FCF3FC",
		svgColor: "#C834CB",
	},
	institute: {
		name: i18nPackage.t("editQuizPage.profileStep.institution.name"),
		description: i18nPackage.t("editQuizPage.profileStep.institution.desc"),
		Icon: InstitutionIcon,
		bgColor: "#F2F1FE",
		svgColor: "#504BB0",
	},
	user: {
		name: i18nPackage.t("editQuizPage.profileStep.user.name"),
		description: i18nPackage.t("editQuizPage.profileStep.user.desc"),
		Icon: PersonalIcon,
		bgColor: "#FAEFE0",
		svgColor: "#8B5D14",
	},
	subAccount: {
		name: i18nPackage.t("editQuizPage.profileStep.subAccount.name"),
		description: i18nPackage.t("editQuizPage.profileStep.subAccount.desc"),
		Icon: SubAccountIcon,
		bgColor: "#ECF4F6",
		svgColor: "#0B6F85",
	},
	preset: {
		advanced: {
			name: i18nPackage.t("editQuizPage.profileStep.advanced.name"),
			description: i18nPackage.t("editQuizPage.profileStep.advanced.desc"),
			Icon: AdvancedIcon,
			bgColor: "#E3F2FD",
			svgColor: "#1565C0",
		},
		standard: {
			name: i18nPackage.t("editQuizPage.profileStep.standard.name"),
			description: i18nPackage.t("editQuizPage.profileStep.standard.desc"),
			Icon: StandardIcon,
			bgColor: "#E3F2FD",
			svgColor: "#1565C0",
		},
		essential: {
			name: i18nPackage.t("editQuizPage.profileStep.essential.name"),
			description: i18nPackage.t("editQuizPage.profileStep.essential.desc"),
			Icon: EssentialIcon,
			bgColor: "#E3F2FD",
			svgColor: "#1565C0",
		},
	},
};

export const THIRD_PARTY_IMAGE_MAP: Record<Platform, React.ReactNode> = {
	[Platform.ALEKS]: <Aleks aria-hidden="true" />,
	[Platform.MCGRAW_HILL]: <McGrawHill aria-hidden="true" />,
	[Platform.PEARSON]: <Pearson aria-hidden="true" />,
	[Platform.CENGAGE]: <Cengage aria-hidden="true" />,
	[Platform.WILEY]: <Wiley aria-hidden="true" />,
	[Platform.MY_OPEN_MATH]: <MyOpenMath aria-hidden="true" />,
	[Platform.WW_NORTON]: <WWNorton aria-hidden="true" />,
	[Platform.MACMILLAN]: <Macmillan aria-hidden="true" />,
	[Platform.LABYRINTH]: <LabyrinthLearning aria-hidden="true" />,
	[Platform.U_CERTIFY]: <UCertify aria-hidden="true" />,
	[Platform.WEBWORK]: <Webwork aria-hidden="true" />,
	[Platform.VHL]: <VHL aria-hidden="true" />,
	[Platform.ZYBOOKS]: <ZyBooks aria-hidden="true" />,
	[Platform.ATOMIC]: <AtomicJolt aria-hidden="true" />,
	[Platform.WILLOLABS]: <WilloLabs aria-hidden="true" />,
	[Platform.LUMINA]: <Others aria-hidden="true" />,
	[Platform.DEFAULT]: <Others aria-hidden="true" />,
};

type SupportedBrowserOSConfigType = {
	name: string;
	Icon: typeof CourseIcon;
	bgColor: string;
	color: string;
};

type SupportedOS = "windows" | "mac";

export const SUPPORTED_OS_CONFIG: Record<SupportedOS, SupportedBrowserOSConfigType> = {
	windows: {
		name: i18nPackage.t("editQuizPage.platformStep.lockdown.windows"),
		Icon: WindowsIcon,
		bgColor: "#E3F2FD",
		color: "#1565C0",
	},
	mac: {
		name: i18nPackage.t("editQuizPage.platformStep.lockdown.macOs"),
		Icon: MacIcon,
		bgColor: "#F3F4F6",
		color: "#374151",
	},
};

type SupportedBrowser = "chrome" | "edge" | "opera" | "brave" | "firefox" | "safari";

export const SUPPORTED_BROWSER_CONFIG: Record<SupportedBrowser, SupportedBrowserOSConfigType> = {
	chrome: {
		name: i18nPackage.t("editQuizPage.platformStep.extension.chrome"),
		Icon: ChromeIcon,
		bgColor: "#E8F5E9",
		color: "#2E7D32",
	},
	edge: {
		name: i18nPackage.t("editQuizPage.platformStep.extension.edge"),
		Icon: EdgeIcon,
		bgColor: "#E3F2FD",
		color: "#1565C0",
	},
	opera: {
		name: i18nPackage.t("editQuizPage.platformStep.extension.opera"),
		Icon: OperaIcon,
		bgColor: "#FCE4EC",
		color: "#C2185B",
	},
	brave: {
		name: i18nPackage.t("editQuizPage.platformStep.extension.brave"),
		Icon: BraveIcon,
		bgColor: "#FFF3E0",
		color: "#E65100",
	},
	firefox: {
		name: i18nPackage.t("editQuizPage.platformStep.extension.firefox"),
		Icon: FirefoxIcon,
		bgColor: "#FFEBEE",
		color: "#C62828",
	},
	safari: {
		name: i18nPackage.t("editQuizPage.platformStep.extension.safari"),
		Icon: SafariIcon,
		bgColor: "#E1F5FE",
		color: "#0277BD",
	},
};

export const PLATFORM_CONFIG: Record<DisplayPlatform, React.ReactNode> = {
	extension: <ExtensionIcon />,
	lockdown: <LockdownIcon />,
	mobile: <LockdownIcon />,
	chromeOs: <LockdownIcon />,
	browser: <BrowserIcon />,
};
